/**
 * DAILY CHECK-IN — a 30-day PROGRESS board that grants PETALS (the premium currency).
 *
 * The board is 30 squares (5 rows × 6 columns). Each check-in advances ONE square and grants that square's
 * Petals. A player may check in ONCE per real calendar day, so checking in daily fills the board fastest —
 * but MISSING a day never resets progress; you simply advance more slowly. After claiming square 30 the board
 * resets to square 1 and the 30-day cycle repeats forever.
 *
 * Row-ends (squares 6, 12, 18, 24) are larger rewards; square 30 is a big jackpot. All amounts are TUNABLE
 * at runtime via the `checkinRewards` live-tuning key (setCheckinRewards), so the economy can be retuned
 * without a deploy — see main.tsx installer + the reward table below.
 *
 * State lives on the save as `checkin: { claimed, totalClaimed, lastClaimDay, streak, longestStreak }`:
 *   • claimed        — how many squares of the CURRENT 30-day cycle have been claimed (0..30). The NEXT square
 *                      to claim is index `claimed` (0-based) → day `claimed + 1`.
 *   • totalClaimed   — squares claimed EVER, across every cycle. Never resets. `claimed` cannot be used to
 *                      count check-ins because it drops to 0 the moment square 30 is claimed, so anything
 *                      asking "how many check-ins has this player made" (the achievement ladder) reads this.
 *   • lastClaimDay   — the local calendar day-number (see localDayNumber) of the last claim; gates once/day.
 *   • streak         — consecutive-calendar-day check-ins (display flavour; does NOT gate rewards).
 *   • longestStreak  — best streak reached (display flavour).
 *
 * WHO DECIDES "ONE PER DAY". The board turns over at the PLAYER'S midnight — that is the feel we want, and
 * `localDayNumber` keeps it. But a local day number is derived from the device: its clock AND its timezone.
 * Rolling the clock forward a day, or hopping to a timezone whose date has already flipped, used to mint
 * another square (250 petals on the jackpot) as often as the player liked, because nothing downstream ever
 * re-checked it. Two things close that:
 *
 *   • THE CLOCK IS THE GAME CLOCK, not the device clock. Everything here defaults to `gameNow()`, which
 *     carries the server offset set by `setServerNow()` on every /api/config and /api/save response
 *     (sim/gameClock.ts). Moving the device clock forward therefore moves nothing once the server has been
 *     heard from — the offset absorbs it. TIMEZONE still comes from the device, on purpose: that is what
 *     makes the board turn over at the player's own midnight rather than in London.
 *   • THE SERVER IS THE ARBITER. Timezone is still device-supplied, so it is still a lever — a player who
 *     hops zones can legitimately see two local midnights inside one 24h stretch. The server therefore keeps
 *     its own record of the last claim it accepted (`checkinGuard`, server/index.js) and pays a changed
 *     `lastClaimDay` only if it is LATER than the last day it paid AND is a day current somewhere on Earth by
 *     the server's clock (`claimDayWindow`: UTC−12 … UTC+14). It does NOT gate on "one per server UTC day" —
 *     that refused honest players whose two consecutive local days fell inside one UTC day. A refused square
 *     is booked on the server's `petalsDebited` ledger, so it comes off the balance on the next load. This
 *     module stays PURE and client-side; it is the optimistic half, and the server has the last word.
 */

import { gameNow } from "./gameClock";

export const CHECKIN_DAYS = 30;
export const CHECKIN_COLS = 6;
export const CHECKIN_ROWS = 5;

/** Per-save check-in progress. All fields optional-safe; a missing block = brand-new (nothing claimed). */
export interface CheckinState {
  claimed: number;        // squares claimed in the current cycle (0..30)
  totalClaimed: number;   // squares claimed EVER (never resets — the cycle reset zeroes `claimed`, not this)
  lastClaimDay: number;   // localDayNumber of the last claim (-1 = never)
  streak: number;         // consecutive-day check-in streak (flavour)
  longestStreak: number;  // best streak reached (flavour)
}

export function freshCheckin(): CheckinState {
  return { claimed: 0, totalClaimed: 0, lastClaimDay: -1, streak: 0, longestStreak: 0 };
}

/** Coerce an unknown blob into a valid CheckinState (all fields clamped/typed). */
export function coerceCheckin(raw: unknown): CheckinState {
  const o = (raw && typeof raw === "object" ? raw : {}) as Partial<CheckinState>;
  const int = (v: unknown, min: number, max: number, dflt: number) =>
    typeof v === "number" && Number.isFinite(v) ? Math.max(min, Math.min(max, Math.round(v))) : dflt;
  const claimed = int(o.claimed, 0, CHECKIN_DAYS, 0);
  return {
    claimed,
    // SAVES PREDATING `totalClaimed` have no lifetime count, so seed it from the current cycle's `claimed`
    // rather than 0 — a player mid-board keeps the progress their achievement row is already showing. It
    // under-counts anyone who has finished a cycle, which is the unavoidable price of the field not existing
    // then; it must never read LOWER than `claimed`, hence the max.
    totalClaimed: Math.max(claimed, int(o.totalClaimed, 0, Number.MAX_SAFE_INTEGER, claimed)),
    lastClaimDay: int(o.lastClaimDay, -1, Number.MAX_SAFE_INTEGER, -1),
    streak: int(o.streak, 0, Number.MAX_SAFE_INTEGER, 0),
    longestStreak: int(o.longestStreak, 0, Number.MAX_SAFE_INTEGER, 0),
  };
}

/**
 * The LOCAL calendar day-number for a timestamp (days since the Unix epoch in the viewer's own timezone). Used
 * to enforce "one check-in per day": two timestamps on the same local date give the same number. We offset by
 * the timezone so midnight LOCAL (not UTC) is the day boundary.
 *
 * `nowMs` should be a GAME timestamp (`gameNow()`, the default) so the DEVICE CLOCK cannot move the boundary
 * — see the header. The device's TIMEZONE still can, and that is deliberate; the server's UTC-day check is
 * what stops it paying twice.
 */
export function localDayNumber(nowMs: number = gameNow()): number {
  const d = new Date(nowMs);
  return Math.floor((nowMs - d.getTimezoneOffset() * 60_000) / 86_400_000);
}

/**
 * The UTC calendar day-number for a timestamp — the same count with no timezone offset applied. This is the
 * unit the SERVER gates on (`checkinGuard` in server/index.js computes it from its own `Date.now()`), and it
 * is exported here so the two halves cannot drift apart: whatever "a day" means to the arbiter is defined by
 * this one line. Never use it for the board's turnover — that is `localDayNumber`'s job.
 */
export function utcDayNumber(nowMs: number = gameNow()): number {
  return Math.floor(nowMs / 86_400_000);
}

// ── REWARD TABLE (tunable) ─────────────────────────────────────────────────────────────────────────────────
/** Petals granted for each square (day 1..30). Row-ends step up; day 30 is the jackpot. */
export interface CheckinRewards {
  /** exactly CHECKIN_DAYS entries — petals for day 1..30. */
  days: number[];
}

/** Default reward curve: gentle daily rise, a bump at each row-end (days 6/12/18/24), jackpot on day 30. */
export const DEFAULT_CHECKIN_REWARDS: CheckinRewards = {
  days: [
    // row 1 (days 1-6)
    10, 12, 14, 16, 18, 40,
    // row 2 (days 7-12)
    15, 17, 19, 21, 23, 60,
    // row 3 (days 13-18)
    20, 22, 24, 26, 28, 90,
    // row 4 (days 19-24)
    25, 27, 29, 31, 33, 130,
    // row 5 (days 25-30) — day 30 is the big jackpot
    30, 33, 36, 39, 42, 250,
  ],
};

/**
 * THE CEILING ON ONE SQUARE — the server's `MAX_CHECKIN_SQUARE` (server/index.js), restated because the
 * server is plain JS. The server sizes its petal rate limit off it, so a square paying more than this is
 * clamped on the server: the player sees the grant, and the next save quietly takes it back. A live
 * `checkinRewards` edit is therefore clamped HERE, at install, so the board never promises what the server
 * will not keep. Raising it means raising the server's copy in the same change (serverMirrors.test.ts).
 */
export const MAX_CHECKIN_PETALS = 250;

// LIVE override (installed from the `checkinRewards` tuning key at boot; see main.tsx). Deep-merged over the
// default so a partial override (e.g. just a couple of days) still yields a full 30-entry table. Each square
// is clamped to MAX_CHECKIN_PETALS — see above.
let REWARDS: CheckinRewards = DEFAULT_CHECKIN_REWARDS;
export function setCheckinRewards(o: Partial<CheckinRewards> | null | undefined): void {
  if (!o || !Array.isArray(o.days)) { REWARDS = DEFAULT_CHECKIN_REWARDS; return; }
  const days = DEFAULT_CHECKIN_REWARDS.days.map((d, i) => {
    const v = o.days![i];
    return typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.min(MAX_CHECKIN_PETALS, Math.round(v)) : d;
  });
  REWARDS = { days };
}
export function getCheckinRewards(): CheckinRewards { return REWARDS; }

/** Petals for a given day index (1-based, 1..30). Out-of-range → 0. */
export function rewardForDay(day1: number): number {
  return REWARDS.days[day1 - 1] ?? 0;
}

/** Is a square a ROW-END milestone (day 6, 12, 18, 24)? Day 30 is the separate final jackpot. */
export function isRowEnd(day1: number): boolean {
  return day1 % CHECKIN_COLS === 0 && day1 !== CHECKIN_DAYS;
}
export function isFinal(day1: number): boolean { return day1 === CHECKIN_DAYS; }

/** Can the player check in RIGHT NOW (i.e. they haven't already claimed today)? */
export function canCheckIn(state: CheckinState, nowMs: number = gameNow()): boolean {
  return state.lastClaimDay !== localDayNumber(nowMs);
}

/** The 1-based day the NEXT claim will grant (1..30). */
export function nextDay(state: CheckinState): number {
  return Math.min(CHECKIN_DAYS, state.claimed + 1);
}

export interface ClaimResult {
  ok: boolean;
  reason?: "already-claimed-today";
  day: number;            // the 1-based square that was claimed
  petals: number;         // petals granted
  cycleComplete: boolean; // true when this claim finished square 30 (board about to reset)
  next: CheckinState;     // the updated state
}

/**
 * Attempt a check-in. Grants the NEXT square's petals if the player hasn't checked in today. Advances the
 * board (resetting to a fresh cycle after square 30), and updates the streak (consecutive if yesterday was the
 * last claim, else reset to 1). PURE — returns the new state + the grant; the caller adds petals + persists.
 */
export function claimCheckin(state: CheckinState, nowMs: number = gameNow()): ClaimResult {
  const today = localDayNumber(nowMs);
  if (state.lastClaimDay === today) {
    return { ok: false, reason: "already-claimed-today", day: nextDay(state), petals: 0, cycleComplete: false, next: state };
  }
  const day = nextDay(state);                      // 1..30 being claimed
  const petals = rewardForDay(day);
  const cycleComplete = day >= CHECKIN_DAYS;
  const claimed = cycleComplete ? 0 : day;         // finishing square 30 resets the board to a fresh cycle
  // … which is why the LIFETIME count is tracked separately: `claimed` goes 29 → 0 on the jackpot claim, so
  // counting check-ins from it can never reach 30. `totalClaimed` only ever goes up.
  const totalClaimed = state.totalClaimed + 1;
  // streak: consecutive if the last claim was YESTERDAY, otherwise this starts a new run of 1.
  const consecutive = state.lastClaimDay === today - 1;
  const streak = consecutive ? state.streak + 1 : 1;
  const longestStreak = Math.max(state.longestStreak, streak);
  return {
    ok: true, day, petals, cycleComplete,
    next: { claimed, totalClaimed, lastClaimDay: today, streak, longestStreak },
  };
}
